import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import StatusBadge from '../components/StatusBadge';
import ScopeSelector from '../components/ScopeSelector';
import ReportPreview from '../components/ReportPreview';

// Dummy-testdata-optie alleen tonen op staging (zelfde vlag als de staging-banner)
const IS_STAGING = process.env.REACT_APP_ENV === 'staging';

// ── Nieuwe audit wizard (3 stappen) ──────────────────────────
function NewAuditModal({ onClose, onSave, t }) {
  const [step, setStep]             = useState(1);
  const [clients, setClients]       = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [form, setForm] = useState({
    name: '', client_id: '', framework_id: '', audit_date: '', language: 'nl', scope: '',
    audit_description: '',
  });
  const [selectedIds, setSelectedIds]         = useState(new Set());
  const [generateQuestions, setGenerateQuestions] = useState(false);
  const [questionCount, setQuestionCount]     = useState(10);
  const [generateDummy,    setGenerateDummy]  = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');

  // Voortgang vragengeneratie
  const [genProgress, setGenProgress] = useState(null); // null | { current, total, label, errors }

  useEffect(() => {
    Promise.all([api.get('/api/clients'), api.get('/api/frameworks')])
      .then(([c, f]) => { setClients(c.data); setFrameworks(f.data); });
  }, []);

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function handleNext(e) { e.preventDefault(); setError(''); setStep(s => s + 1); }

  async function handleSubmit() {
    if (selectedIds.size === 0) { setError('Selecteer minstens één control.'); return; }
    setLoading(true); setError('');

    // ── Stap 1: audit aanmaken ───────────────────────────────
    let auditId;
    try {
      const res = await api.post('/api/audits', {
        ...form,
        control_ids: [...selectedIds],
        question_count: questionCount,
      });
      auditId = res.data.id;
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
      setLoading(false);
      return;
    }

    // ── Stap 2: dummy data genereren ────────────────────────
    if (generateDummy) {
      try {
        await api.post(`/api/audits/${auditId}/dummy-data`);
      } catch (err) {
        setError('Dummy data genereren mislukt — audit is aangemaakt maar findings zijn leeg.');
        setLoading(false);
        return; // stop hier, laat gebruiker zelf navigeren
      }
    }

    // ── Stap 3: auditvragen genereren (niet-fataal) ──────────
    if (generateQuestions && questionCount > 0) {
      try {
        const findingsRes = await api.get(`/api/audits/${auditId}/findings`);
        const allFindings = findingsRes.data;
        const total = allFindings.length;
        let done = 0, errors = 0;
        for (const f of allFindings) {
          setGenProgress({ current: done, total, label: f.control_id, errors });
          try {
            await api.post(`/api/audits/${auditId}/generate-questions`, {
              finding_ids: [f.id],
              question_count: questionCount,
            });
          } catch (err) {
            console.warn(`Auditvragen mislukt voor ${f.control_id}:`, err);
            errors++;
          }
          done++;
          setGenProgress({ current: done, total, label: f.control_id, errors });
        }
      } catch (err) {
        console.warn('Auditvragen genereren mislukt:', err);
      }
    }

    onSave(auditId);
  }

  const selectedFramework = frameworks.find(f => String(f.id) === String(form.framework_id));
  const selectedClient    = clients.find(c => String(c.id) === String(form.client_id));

  const stepLabels = ['Basisgegevens', `Scope${selectedFramework ? ` · ${selectedFramework.name}` : ''}`, 'Auditvragen'];

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">

            {/* Header */}
            <div className="modal-header">
              <div>
                <h5 className="modal-title mb-0">{t('audit_new')}</h5>
                <div className="text-muted small mt-1">
                  Stap {step} van 3 — {stepLabels[step - 1]}
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
            </div>

            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

              {/* Voortgang vragengeneratie */}
              {genProgress && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between small text-muted mb-1">
                    <span>
                      <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} />
                      Control <strong>{genProgress.label}</strong> genereren…
                      {genProgress.errors > 0 && (
                        <span className="text-danger ms-2">{genProgress.errors} fout</span>
                      )}
                    </span>
                    <span>{genProgress.current} / {genProgress.total} controls</span>
                  </div>
                  <div className="progress" style={{ height: 6 }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      style={{ width: `${Math.round((genProgress.current / genProgress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Stap 1 — Basisgegevens */}
              {step === 1 && (
                <form id="audit-step1" onSubmit={handleNext}>
                  <div className="mb-3">
                    <label className="form-label small fw-medium">{t('audit_name')} *</label>
                    <input className="form-control" value={form.name}
                      onChange={e => set('name', e.target.value)}
                      required placeholder="bv. ISO 27001 audit Q2 2025" autoFocus />
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col">
                      <label className="form-label small fw-medium">{t('audit_client')} *</label>
                      <select className="form-select" value={form.client_id}
                        onChange={e => set('client_id', e.target.value)} required>
                        <option value="">—</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="col">
                      <label className="form-label small fw-medium">{t('audit_framework')} *</label>
                      <select className="form-select" value={form.framework_id}
                        onChange={e => { set('framework_id', e.target.value); setSelectedIds(new Set()); }} required>
                        <option value="">—</option>
                        {frameworks.map(f => (
                          <option key={f.id} value={f.id}>{f.name}{f.version ? ` (${f.version})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col">
                      <label className="form-label small fw-medium">{t('audit_date')}</label>
                      <input type="date" className="form-control" value={form.audit_date}
                        onChange={e => set('audit_date', e.target.value)} />
                    </div>
                    <div className="col">
                      <label className="form-label small fw-medium">{t('audit_language')}</label>
                      <select className="form-select" value={form.language}
                        onChange={e => set('language', e.target.value)}>
                        <option value="nl">Nederlands</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>
                </form>
              )}

              {/* Stap 2 — Scope */}
              {step === 2 && form.framework_id && (
                <ScopeSelector
                  frameworkId={form.framework_id}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              )}

              {/* Stap 3 — Auditvragen */}
              {step === 3 && (
                <div>
                  <div className="mb-4">
                    <label className="form-label small fw-medium">
                      Omschrijving van deze auditronde
                    </label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder={`Welke afdeling of domein audit je? Wat is de aanleiding?\nbv. "IT-afdeling, focus op toegangsbeheer en back-ups" of "HR-processen i.k.v. ISO 27001 A.6"`}
                      value={form.audit_description}
                      onChange={e => set('audit_description', e.target.value)}
                      autoFocus
                    />
                    <div className="form-text">
                      Wordt meegestuurd als context naar de AI bij het genereren van vragen.
                    </div>
                  </div>

                  <div className="card border-0 bg-light p-3">
                    {IS_STAGING && (
                      <div className="form-check mb-3">
                        <input className="form-check-input" type="checkbox" id="genDummy"
                          checked={generateDummy}
                          onChange={e => setGenerateDummy(e.target.checked)} />
                        <label className="form-check-label fw-medium" htmlFor="genDummy">
                          🧪 Vul dummy testdata in (willekeurige notities + status per control)
                        </label>
                        <div className="text-muted small mt-1">
                          Handig om de Word/PDF-template te testen. Na aanmaken kun je meteen een Polish doen.
                        </div>
                      </div>
                    )}

                    <div className="form-check mb-3">
                      <input className="form-check-input" type="checkbox" id="genQ"
                        checked={generateQuestions}
                        onChange={e => setGenerateQuestions(e.target.checked)} />
                      <label className="form-check-label fw-medium" htmlFor="genQ">
                        ✨ Genereer auditvragen voor deze klant en audit
                      </label>
                      {selectedClient && !selectedClient.description && (
                        <div className="text-warning small mt-1">
                          <i className="bi bi-exclamation-triangle me-1" />
                          Tip: voeg een bedrijfsomschrijving toe aan de klant voor betere vragen.
                        </div>
                      )}
                    </div>

                    {generateQuestions && (
                      <div style={{ maxWidth: 260 }}>
                        <label className="form-label small fw-medium">Aantal vragen per control</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min={1} max={10}
                          value={questionCount}
                          onChange={e => setQuestionCount(Math.max(1, Math.min(10, +e.target.value)))}
                        />
                        <div className="form-text">
                          Elke control krijgt eigen, specifieke vragen. Max. 10 per control.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              {step > 1 && !loading && (
                <button type="button" className="btn btn-secondary" onClick={() => setStep(s => s - 1)}>
                  <i className="bi bi-arrow-left me-1" />Terug
                </button>
              )}
              {step === 1 && (
                <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              )}

              {step < 3 ? (
                step === 1 ? (
                  <button type="submit" form="audit-step1" className="btn btn-primary">
                    Volgende: Scope <i className="bi bi-arrow-right ms-1" />
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={() => { setError(''); setStep(3); }}
                    disabled={selectedIds.size === 0}>
                    Volgende: Auditvragen <i className="bi bi-arrow-right ms-1" />
                  </button>
                )
              ) : (
                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2" />
                        {genProgress
                          ? `${genProgress.current}/${genProgress.total} controls…`
                          : 'Aanmaken…'}
                      </>
                    : <>{t('audit_save')} ({selectedIds.size} controls)</>}
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

// ── Sorteer-header component ──────────────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort, style }) {
  const active = sortCol === col;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        onClick={() => onSort(col)}>
      {label}
      {active
        ? <i className={`bi bi-caret-${sortDir === 'asc' ? 'up' : 'down'}-fill ms-1`} style={{ fontSize: 10 }} />
        : <i className="bi bi-caret-up ms-1 text-muted" style={{ fontSize: 10, opacity: 0.4 }} />}
    </th>
  );
}

// ── Audit List pagina ────────────────────────────────────────
export default function AuditListPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [audits, setAudits]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter]   = useState('all');

  // Sortering
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Filters
  const [filterName,      setFilterName]      = useState('');
  const [filterClient,    setFilterClient]    = useState('');
  const [filterFramework, setFilterFramework] = useState('');

  function load() {
    api.get('/api/audits').then(r => setAudits(r.data)).finally(() => setLoading(false));
  }

  useEffect(load, []);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const [showArchived, setShowArchived] = useState(false);
  const STATUSES = ['draft', 'in_progress', 'review', 'closed'];

  // Archiveren / verwijderen
  const [confirmTarget, setConfirmTarget] = useState(null); // null | { audit, action }
  const [actionLoading, setActionLoading] = useState(false);
  const [preview, setPreview] = useState(null); // null | { id, filename }

  async function handleConfirmedAction() {
    if (!confirmTarget) return;
    const { audit, action } = confirmTarget;
    setActionLoading(true);
    try {
      if (action === 'archive') {
        await api.patch(`/api/audits/${audit.id}/archive`);
      } else if (action === 'unarchive') {
        await api.patch(`/api/audits/${audit.id}/unarchive`);
      } else if (action === 'delete') {
        await api.delete(`/api/audits/${audit.id}`);
      }
      setConfirmTarget(null);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Er is een fout opgetreden');
      setConfirmTarget(null);
    } finally {
      setActionLoading(false);
    }
  }

  // Unieke waarden voor dropdowns
  const clientOptions    = useMemo(() => [...new Set(audits.map(a => a.client_name).filter(Boolean))].sort(), [audits]);
  const frameworkOptions = useMemo(() => [...new Set(audits.map(a => a.framework_name).filter(Boolean))].sort(), [audits]);

  const displayed = useMemo(() => {
    let list = filter === 'all'
      ? audits.filter(a => showArchived ? a.status === 'archived' : a.status !== 'archived')
      : audits.filter(a => a.status === filter);
    if (filterName)      list = list.filter(a => a.name?.toLowerCase().includes(filterName.toLowerCase()));
    if (filterClient)    list = list.filter(a => a.client_name === filterClient);
    if (filterFramework) list = list.filter(a => a.framework_name === filterFramework);

    list = [...list].sort((a, b) => {
      let va = a[sortCol] ?? '';
      let vb = b[sortCol] ?? '';
      // Numeriek voor progress-kolommen
      if (['beoordeeld', 'total_controls', 'niet_conform'].includes(sortCol)) {
        va = Number(va); vb = Number(vb);
      } else {
        va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [audits, filter, showArchived, filterName, filterClient, filterFramework, sortCol, sortDir]);

  const hasFilters = filterName || filterClient || filterFramework;

  return (
    <>
      <div className="page-header">
        <h2>{t('audits_title')}</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>
          <i className="bi bi-plus me-1" />{t('audit_new')}
        </button>
      </div>

      <div className="page-body">
        {/* Status-chips */}
        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <button
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFilter('all')}
          >{t('all')}</button>
          {STATUSES.map(s => (
            <button key={s}
              className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => { setFilter(s); setShowArchived(false); }}
            >{t(`status_${s}`)}</button>
          ))}
          <button
            className={`btn btn-sm ${showArchived ? 'btn-secondary' : 'btn-outline-secondary'}`}
            onClick={() => { setShowArchived(a => !a); setFilter('all'); }}
          >
            <i className="bi bi-archive me-1" />Archief
          </button>
          {hasFilters && (
            <button className="btn btn-sm btn-outline-danger ms-2"
              onClick={() => { setFilterName(''); setFilterClient(''); setFilterFramework(''); }}>
              <i className="bi bi-x me-1" />Filters wissen
            </button>
          )}
        </div>

        <div className="card">
          {loading ? (
            <div className="card-body text-center text-muted py-4">
              <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          ) : audits.length === 0 ? (
            <div className="card-body text-center text-muted py-4">
              <button className="btn btn-link p-0" onClick={() => setShowNew(true)}>+ {t('audit_new')}</button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-clickable mb-0">
                <thead className="table-light">
                  <tr>
                    <SortTh col="name"          label={t('audit_name')}      sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="client_name"   label={t('audit_client')}    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="framework_name" label={t('audit_framework')} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="audit_date"    label={t('audit_date')}      sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="status"        label={t('audit_status')}    sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <SortTh col="beoordeeld"    label={t('audit_progress')}  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ minWidth: 140 }} />
                    <SortTh col="last_activity" label="Laatst bewerkt"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                    <th style={{ width: 80 }} />
                  </tr>
                  {/* Filterrij */}
                  <tr style={{ background: '#f8fafc' }}>
                    <td>
                      <input className="form-control form-control-sm" placeholder="Zoek naam…"
                        value={filterName} onChange={e => setFilterName(e.target.value)} />
                    </td>
                    <td>
                      <select className="form-select form-select-sm" value={filterClient}
                        onChange={e => setFilterClient(e.target.value)}>
                        <option value="">Alle klanten</option>
                        {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="form-select form-select-sm" value={filterFramework}
                        onChange={e => setFilterFramework(e.target.value)}>
                        <option value="">Alle frameworks</option>
                        {frameworkOptions.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                    <td /><td /><td /><td /><td />
                  </tr>
                </thead>
                <tbody>
                  {displayed.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-3">Geen resultaten</td></tr>
                  ) : displayed.map(a => {
                    const pct = a.total_controls > 0
                      ? Math.round((a.beoordeeld / a.total_controls) * 100) : 0;
                    return (
                      <tr key={a.id} onClick={() => navigate(`/audits/${a.id}`)}>
                        <td>
                          <Link to={`/audits/${a.id}`} className="fw-medium text-decoration-none"
                            onClick={e => e.stopPropagation()}>
                            {a.name}
                          </Link>
                        </td>
                        <td>{a.client_name}</td>
                        <td className="text-muted">{a.framework_name}</td>
                        <td className="text-muted">{a.audit_date ? new Date(a.audit_date).toLocaleDateString() : '—'}</td>
                        <td><StatusBadge status={a.status} /></td>
                        <td>
                          <div className="progress">
                            <div className="progress-bar" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-muted-sm mt-1">
                            {a.beoordeeld}/{a.total_controls} {t('assessed')} — {a.niet_conform} ✗
                          </div>
                        </td>
                        <td className="text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {a.last_activity
                            ? new Date(a.last_activity).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })
                            : '—'}
                        </td>
                        <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          <div className="d-flex gap-1">
                            {a.last_report_id && (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title={t('preview')}
                                onClick={() => setPreview({ id: a.last_report_id, filename: `${a.name}.${a.last_report_format || 'pdf'}` })}
                              >
                                <i className="bi bi-eye" />
                              </button>
                            )}
                            {a.status !== 'archived' ? (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Archiveren"
                                onClick={() => setConfirmTarget({ audit: a, action: 'archive' })}
                              >
                                <i className="bi bi-archive" />
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Terugzetten naar concept"
                                onClick={() => setConfirmTarget({ audit: a, action: 'unarchive' })}
                              >
                                <i className="bi bi-arrow-counterclockwise" />
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Definitief verwijderen"
                              onClick={() => setConfirmTarget({ audit: a, action: 'delete' })}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <NewAuditModal
          t={t}
          onClose={() => setShowNew(false)}
          onSave={id => { setShowNew(false); navigate(`/audits/${id}`); }}
        />
      )}

      {preview && (
        <ReportPreview
          reportId={preview.id}
          filename={preview.filename}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Bevestigingsmodal voor archiveren / verwijderen */}
      {confirmTarget && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {confirmTarget.action === 'archive'   && 'Audit archiveren'}
                    {confirmTarget.action === 'unarchive' && 'Audit terugzetten'}
                    {confirmTarget.action === 'delete'    && 'Audit verwijderen'}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setConfirmTarget(null)} disabled={actionLoading} />
                </div>
                <div className="modal-body">
                  {confirmTarget.action === 'archive' && (
                    <p>De audit <strong>{confirmTarget.audit.name}</strong> wordt gearchiveerd en is niet meer zichtbaar in de standaardlijst. Je kunt hem later terugzetten.</p>
                  )}
                  {confirmTarget.action === 'unarchive' && (
                    <p>De audit <strong>{confirmTarget.audit.name}</strong> wordt teruggezet naar de status <em>Concept</em>.</p>
                  )}
                  {confirmTarget.action === 'delete' && (
                    <>
                      <p>Je staat op het punt om de audit <strong>{confirmTarget.audit.name}</strong> <span className="text-danger fw-bold">definitief te verwijderen</span>. Dit verwijdert ook alle bevindingen en bewijsstukken.</p>
                      <p className="text-danger small mb-0">Deze actie kan niet ongedaan worden gemaakt.</p>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirmTarget(null)} disabled={actionLoading}>
                    Annuleren
                  </button>
                  <button
                    type="button"
                    className={`btn ${confirmTarget.action === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                    onClick={handleConfirmedAction}
                    disabled={actionLoading}
                  >
                    {actionLoading && <span className="spinner-border spinner-border-sm me-2" />}
                    {confirmTarget.action === 'archive'   && 'Archiveren'}
                    {confirmTarget.action === 'unarchive' && 'Terugzetten'}
                    {confirmTarget.action === 'delete'    && 'Definitief verwijderen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      )}
    </>
  );
}
